<template>
  <ClientOnly>
    <div>
      <Dropdown position="right">
        <DropdownToggler :class="actionClasses" data-test="actions">
          <span v-if="status" class="war-room-post-actions__status">{{ status }}</span>
          <MoreIcon class="war-room-post-actions__icon" />
        </DropdownToggler>

        <template #menu>
          <DropdownMenu>
            <DropdownItem
              v-for="(action, index) in actions"
              :key="index"
              data-test="action-item"
              @click="action.action"
            >
              <div class="war-room-post-actions__icon-wrapper">
                <Component :is="action.icon" class="war-room-post-actions__dropdown-icon" />
              </div>
              {{ action.name }}
            </DropdownItem>
          </DropdownMenu>
        </template>
      </Dropdown>
      <FlagIssueModal />
    </div>
  </ClientOnly>
</template>

<script lang="ts" setup>
import { computed, useStore, useContext, useRouter } from '@nuxtjs/composition-api';
import type { Component as ComponentType } from 'vue';
import Dropdown from '@/components/common/Dropdown/Dropdown.vue';
import DropdownMenu from '@/components/common/Dropdown/DropdownMenu.vue';
import DropdownToggler from '@/components/common/Dropdown/DropdownToggler.vue';
import DropdownItem from '@/components/common/Dropdown/DropdownItem.vue';
import MoreIcon from '@/assets/svg/more.vue.svg';
import FlagIcon from '@/assets/svg/u_flag.vue.svg';
import TrashIcon from '@/assets/svg/u_trash-alt.vue.svg';
import EditIcon from '@/assets/svg/u_comment-edit.vue.svg';
import FlagIssueModal from '@/components/common/FlagIssueModal.vue';
import { confirmNames } from '@/components/partials/war-room/constants';
import { isExtraRoleAdmin } from '@/components/partials/war-room/utils';
import type { IPost } from '@/types/community';
import type { RootState } from '~/types/store';

type Action = {
  name: string;
  action: Function;
  visible: boolean;
  icon?: ComponentType;
};

type WarRoomPostActionsProps = {
  post: IPost;
  className?: string;
};

const props = withDefaults(defineProps<WarRoomPostActionsProps>(), {
  className: 'war-room-post-actions',
});

const { $auth, $modal } = useContext();
const router = useRouter();
const store = useStore<RootState>();

const CUSTOM_WEIGHT_DOSE = 10;
const BRAVOS_DOSE = 5;

function handleWeight(weight: number): void {
  store.dispatch('warRoom/updatePostStats', { post: props.post, attributes: { custom_weight: weight } });
}

function handleToggleHide() {
  store.dispatch('warRoom/updatePostStats', {
    post: props.post,
    attributes: { hidden_from_feed: !props.post.hidden_from_feed },
  });
}

const hasExtraActions = computed(() => Boolean($auth.user?.admin) && isExtraRoleAdmin());

const status = computed(() => (hasExtraActions.value ? props.post.algorithm_metadata?.title || '' : ''));
const actionClasses = computed(() => ({
  [props.className]: true,
  'war-room-post-actions__more': hasExtraActions.value,
}));
const isAuthor = computed(() => {
  if (!$auth.loggedIn) {
    return false;
  }
  return (
    props.post.author?.slug === $auth.user.slug ||
    props.post.author?.username === $auth.user.username ||
    (Boolean($auth.user.admin) && isExtraRoleAdmin())
  );
});

function handleEdit() {
  router.push(`/war-room/posts/edit/${props.post.slug}`);
}

function openConfirmDeleteDialog() {
  $modal.showWithRoute({ custom: confirmNames.DELETE_POST }, { post_slug: props.post.slug });
}

function handleRating() {
  store.dispatch('warRoom/addMassUpvote', { post: props.post, attributes: { mass_upvote_count: BRAVOS_DOSE } });
}

function handleTogglePin() {
  store.dispatch('warRoom/updatePostStats', { post: props.post, attributes: { pinned: !props.post.pinned } });
}

function openFlagIssueModal() {
  $modal.showWithRoute(
    { custom: 'flag-issue' },
    { flaggableId: String(props.post.id), flaggableType: 'WarRoom::Post' },
  );
}

const actions = computed<Action[]>(() => {
  return [
    {
      name: `Move Up +${CUSTOM_WEIGHT_DOSE}`,
      action: () => handleWeight(CUSTOM_WEIGHT_DOSE),
      visible: hasExtraActions.value,
    },
    {
      name: `Move Down -${CUSTOM_WEIGHT_DOSE}`,
      action: () => handleWeight(-CUSTOM_WEIGHT_DOSE),
      visible: hasExtraActions.value,
    },
    {
      name: '+1',
      action: () => handleWeight(1),
      visible: hasExtraActions.value,
    },
    {
      name: '-1',
      action: () => handleWeight(-1),
      visible: hasExtraActions.value,
    },
    {
      name: 'Mass Bravos',
      action: handleRating,
      visible: hasExtraActions.value,
    },
    {
      name: 'Hide from feed',
      action: handleToggleHide,
      visible: hasExtraActions.value && !props.post.hidden_from_feed,
    },
    {
      name: 'Unhide from feed',
      action: handleToggleHide,
      visible: hasExtraActions.value && props.post.hidden_from_feed,
    },
    {
      name: 'Pin',
      action: handleTogglePin,
      visible: hasExtraActions.value && !props.post.pinned,
    },
    {
      name: 'Unpin',
      action: handleTogglePin,
      visible: hasExtraActions.value && props.post.pinned,
    },
    {
      name: 'Edit',
      action: handleEdit,
      visible: isAuthor.value,
      icon: EditIcon,
    },
    {
      name: 'Remove',
      action: openConfirmDeleteDialog,
      visible: isAuthor.value,
      icon: TrashIcon,
    },
    {
      name: 'Flag an issue',
      action: openFlagIssueModal,
      visible: !isAuthor.value,
      icon: FlagIcon,
    },
  ].filter(a => a.visible);
});
</script>

<style scoped lang="scss">
.war-room-post-actions {
  &__more {
    display: flex;
    align-items: center;
  }

  &__icon-wrapper {
    width: 32px;
    height: 32px;
    display: inline-flex;
    justify-content: center;
    align-items: center;
    padding: 4px;
    border-radius: 6px;
    margin-right: 8px;
    background-color: var(--theme-color-background);
    vertical-align: middle;
  }

  &__dropdown-icon {
    color: var(--theme-color-sub-text);
    width: 24px;
    height: 24px;
  }

  &__status {
    @include caption1-semibold;

    position: absolute;
    white-space: nowrap;
    top: -20px;
    right: 0;
    background: var(--theme-color-background);
  }

  &__icon {
    width: 18px;
    height: 18px;
    color: var(--theme-color-sub-text);
  }
}
</style>
